import React, { useId } from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * WorkspaceSwitch — segmented control at the bottom of the dark sidebar that
 * switches between workspaces (ticket #295: Projects | Bids). Selected =
 * filled primary with white text; unselected = transparent, sidebar gray.
 * Placement (divider, padding) is up to the sidebar.
 *
 * Props:
 *   options    {Array<{ value, label, icon }>}  icon: Outlined icon element
 *   value      {any}    selected option value
 *   onChange   {func}   (value) => void; not called for the selected option
 *   collapsed  {bool}   icon-only vertical stack, each button with a tooltip
 *   label      {string} group label, shown uppercase when expanded ("Workspace")
 *   sx         {object} extra styles for the root
 */
const WorkspaceSwitch = ({
  options = [],
  value,
  onChange,
  collapsed = false,
  label = 'Workspace',
  sx,
}) => {
  const { ft } = useTheme().palette;
  const labelId = useId();

  const renderButton = (option) => {
    const selected = option.value === value;
    const button = (
      <ButtonBase
        key={collapsed ? undefined : String(option.value)}
        aria-pressed={selected}
        aria-label={collapsed ? option.label : undefined}
        onClick={() => {
          if (!selected && onChange) onChange(option.value);
        }}
        sx={{
          height: collapsed ? 36 : 30,
          width: collapsed ? 40 : 'auto',
          px: collapsed ? 0 : 1,
          gap: 0.75,
          borderRadius: `${ft.radius.control}px`,
          fontFamily: ft.fontFamily,
          fontSize: 13,
          fontWeight: ft.fontWeights.medium,
          backgroundColor: selected ? ft.primary.main : 'transparent',
          color: selected ? ft.sidebar.textActive : ft.sidebar.text,
          '& .MuiSvgIcon-root': { fontSize: collapsed ? 20 : 16 },
          '&:hover': {
            backgroundColor: selected ? ft.primary.dark : ft.sidebar.activeBg,
            color: ft.sidebar.textActive,
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${ft.primary.main}`,
            outlineOffset: 1,
          },
        }}
      >
        {option.icon}
        {!collapsed && option.label}
      </ButtonBase>
    );

    return collapsed ? (
      <Tooltip
        key={String(option.value)}
        title={option.label}
        placement="right"
      >
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  if (collapsed) {
    return (
      <Box
        role="group"
        aria-label={label}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          p: '3px',
          backgroundColor: ft.sidebar.track,
          borderRadius: '6px',
          ...sx,
        }}
      >
        {options.map(renderButton)}
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      <Typography
        id={labelId}
        variant="sectionLabel"
        component="div"
        sx={{ color: ft.sidebar.text, mb: 0.75, ml: '2px' }}
      >
        {label}
      </Typography>
      <Box
        role="group"
        aria-labelledby={labelId}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${options.length || 1}, minmax(0, 1fr))`,
          gap: '2px',
          p: '3px',
          backgroundColor: ft.sidebar.track,
          borderRadius: '6px',
        }}
      >
        {options.map(renderButton)}
      </Box>
    </Box>
  );
};

export default WorkspaceSwitch;
